import moment from 'moment';
import React, { useState, useEffect } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from 'react-redux';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Colxx } from '../../../../components/common/CustomBootstrap';
import { firestore } from '../../../../helpers/Firebase';
import { accessTypesObject, createNotification } from '../../../../helpers/Utils';

const ManageBusyTimings = ({ washer, dismiss }) => {
    const { user } = useSelector(state => state.authUser);
    
    // Initialize dates from existing busy timings if available
    const getInitialStartDate = () => {
        if (washer?.busyTimings?.startTime) {
            return new Date(washer.busyTimings.startTime);
        }
        return new Date();
    };
    
    const getInitialEndDate = () => {
        if (washer?.busyTimings?.endTime) {
            return new Date(washer.busyTimings.endTime);
        }
        const end = new Date();
        end.setDate(end.getDate() + 3);
        return end;
    };
    
    const [startDate, setStartDate] = useState(getInitialStartDate);
    const [endDate, setEndDate] = useState(getInitialEndDate);
    const [minTime, setMinTime] = useState(() => calculateMinTime(getInitialStartDate()));
    
    // Update dates when washer prop changes
    useEffect(() => {
        if (washer?.busyTimings?.startTime && washer?.busyTimings?.endTime) {
            const newStartDate = new Date(washer.busyTimings.startTime);
            const newEndDate = new Date(washer.busyTimings.endTime);
            setStartDate(newStartDate);
            setEndDate(newEndDate);
            setMinTime(calculateMinTime(newStartDate));
        }
    }, [washer]);

    function calculateMinTime(date) {
        let isToday = moment(date).isSame(moment(), 'day');
        if (isToday) {
            let nowAddOneHour = moment(new Date()).add({ hours: 1 }).toDate();
            return nowAddOneHour;
        }
        return moment().startOf('day').toDate();
    }

    const handleStartDateChange = (date) => {
        setStartDate(date);
        setMinTime(calculateMinTime(date));
    };

    const handleSchedule = async () => {
        let post = {
            updated: Date.now(),
            busyTimings: {
                startTime: moment(startDate).toDate().getTime(),
                endTime: moment(endDate).toDate().getTime()
            }
        };

        var sfDocRef = firestore().collection('washers').doc(washer?.key);
        try {
            await firestore().runTransaction(async (transaction) => {
                // This code may get re-run multiple times if there are conflicts.
                const sfDoc = await transaction.get(sfDocRef);
                if (!sfDoc.exists) {
                    throw new Error("Document does not exist!");
                }
                transaction.update(sfDocRef, post);
            });
            
            let id = firestore().collection('washers').doc().id;
            firestore().collection('washers').doc(washer.key).collection("busyTimingsLogs").doc(id).set({
                busyTimings: post.busyTimings,
                created: Date.now(),
                title: 'Applied Busy Timings',
                createdBy: accessTypesObject?.[user?.accessType]?.name,
                createdByName: user?.displayName
            }, { merge: true }).then(() => {
                createNotification('success', 'Worker busy timings scheduled successfully from ' + moment(startDate).format('DD/MM/YYYY hh:mm a') + ' to ' + moment(endDate).format('DD/MM/YYYY hh:mm a'));
            });

            dismiss();
        } catch (error) {
            createNotification('error', 'Your request could not be processed, please try again or contact customer support');
        }
    };

    const handleClearBusyTimings = async () => {
        var sfDocRef = firestore().collection('washers').doc(washer?.key);
        try {
            await firestore().runTransaction(async (transaction) => {
                // This code may get re-run multiple times if there are conflicts.
                const sfDoc = await transaction.get(sfDocRef);
                if (!sfDoc.exists) {
                    throw new Error("Document does not exist!");
                }
                transaction.update(sfDocRef, { active: true, busyTimings: false, updated: Date.now() });
            });
            let id = firestore().collection('washers').doc().id;
            firestore().collection('washers').doc(washer.key).collection("busyTimingsLogs").doc(id).set({
                busyTimings: false,
                created: Date.now(),
                title: 'Removed Busy Timings',
                createdBy: accessTypesObject?.[user?.accessType]?.name,
                createdByName: user?.displayName

            }, { merge: true });

           
            createNotification('success', 'Busy timings cleared successfully for ' + washer.name);
            dismiss();
        } catch (error) {
            createNotification('error', 'Your request could not be processed, please try again or contact customer support');
        }
    };

    if (!washer) return null;

    return (
        <Modal isOpen={true}>
            <ModalHeader>Schedule Busy Timings for {washer.name}</ModalHeader>
            <ModalBody>


                <Row>
                    <Colxx xxs="12" md="6" style={{ position: 'auto' }}>
                        <div>Start date and time</div>
                        <DatePicker
                            selected={startDate}
                            popperClassName="popperClass"
                            portalId="datepicker-portal"
                            popperPlacement="bottom-start"
                            popperModifiers={[
                                { name: 'offset', options: { offset: [0, 8] } },
                                { name: 'flip', enabled: false },
                                { name: 'preventOverflow', options: { padding: 8 } },
                            ]}
                            onChange={handleStartDateChange}
                            selectsStart
                            startDate={startDate}
                            minDate={moment().toDate()}
                            minTime={minTime}
                            maxTime={moment().endOf('day').toDate()}
                            endDate={endDate}
                            showTimeSelect
                            timeFormat="HH:mm"
                            className="customDatePicker"
                            timeIntervals={15}
                            timeCaption="time"
                            dateFormat="dd/MM/yyyy hh:mm a"
                        />
                    </Colxx>
                    <Colxx xxs="12" md="6">
                        <div>End date and time</div>
                        <DatePicker
                            selected={endDate}
                            popperClassName="popperClass"
                            portalId="datepicker-portal"
                            popperPlacement="bottom-start"
                            popperModifiers={[
                                { name: 'offset', options: { offset: [0, 8] } },
                                { name: 'flip', enabled: false },
                                { name: 'preventOverflow', options: { padding: 8 } },
                            ]}
                            onChange={setEndDate}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            className="customDatePicker"
                            minDate={startDate}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            timeCaption="time"
                            dateFormat="dd/MM/yyyy hh:mm a"
                        />
                    </Colxx>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button outline color='danger' className='m-1' onClick={dismiss}>Cancel</Button>
                {washer.busyTimings && washer.busyTimings !== false && (
                    <Button outline color='secondary' className='m-1' onClick={handleClearBusyTimings}>Clear Busy Timings</Button>
                )}
                <Button color='primary' className='m-1' onClick={handleSchedule}>Schedule</Button>
            </ModalFooter>
        </Modal>
    );
};

export default ManageBusyTimings;
